import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { RegulifyService } from "../../api/RegulifyService";
import { useClinicalEvaluationStore } from "../../store/useClinicalEvaluationStore";
import { getSelectedStudyProductName } from "../../utils/clinicalStudy";
import HistoryAndDetails from "../HistoryAndDetails";
import QueryBox from "../QueryBox";
import { useLoading } from "../../context/LoadingContext";
import { useJobProgress } from "../../hooks/useJobProgress";
import { toast } from "sonner";

function hasBalancedParentheses(query) {
  let count = 0;

  for (const char of query) {
    if (char === '(') count++;
    if (char === ')') count--;
    if (count < 0) return false; // closing before opening
  }

  return count === 0; // must end balanced
}

export default function AdvancedSearch() {
  const [query, setQuery] = useState("");
  const [history, setHistory] = useState([]);
  const { loading, setLoading, setLoadingProgress } = useLoading();
  const { subscribeToJob } = useJobProgress();
  const selectedStudy = useClinicalEvaluationStore(
    useShallow((state) => state.selectedStudy),
  );
  const studyName = getSelectedStudyProductName(selectedStudy);
  const settings = selectedStudy?.settings || {};
  const normalizePicoTerm = (value) => {
    const term = String(value || "").trim();
    if (!term) return "";
    const hasParens = term.startsWith("(") && term.endsWith(")");
    return hasParens ? term : `(${term})`;
  };

  const picoQuery = (() => {
    const full = String(settings.full_search_query || "").trim();
    if (full) return full;

    const populationTerm =
      settings.population_term || settings.population || settings.users || settings.users_description || "";
    const interventionTerm =
      settings.intervention_term || settings.intervention || "";
    const comparatorTerm =
      settings.comparator_term || settings.comparator || "";
    const outcomesTerm =
      settings.outcomes_term || settings.outcomes || settings.outcome || "";

    const terms = [
      normalizePicoTerm(populationTerm),
      normalizePicoTerm(interventionTerm),
      normalizePicoTerm(comparatorTerm),
      normalizePicoTerm(outcomesTerm),
    ].filter(Boolean);

    return terms.length ? terms.join(" AND ") : "";
  })();

  // Load previous history from backend
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        if (!studyName) {
          toast.error("studyName is missing!");
          return;
        }

        if (!hasBalancedParentheses(query)) {
          toast.error("Invalid search term defined.. checking opening '(' or ')' brackets");
          return;
        }

        setLoading(true, "Loading search history..."); // Set loading before history fetch
        const response =
          await RegulifyService.cle_get_search_history(studyName);
        const items = response.data.items || [];

        // Map backend keys to frontend state keys
        const mappedHistory = items.map((item) => ({
          query: item.search_term,
          resultCount: item.found_litr_count,
          timestamp: new Date().toISOString(), // or your backend timestamp if available
        }));

        setHistory(mappedHistory);
      } catch (err) {
        console.error("Failed to load history:", err);
      } finally {
        setLoading(false); // Clear loading after history fetch
      }
    };
    fetchHistory();
  }, []);

  const handleSearch = async () => {
    if (!query.trim()) return;
    if (!studyName) {
      toast.error("studyName is missing!");
      return;
    }
    if (!hasBalancedParentheses(query)) {
      toast.error("Invalid search term defined.. checking opening '(' or ')' brackets");
      return;
    }

    setLoading(true, "Starting literature search…");

    try {
      // Backend enqueues this as a background job (PubMed + Europe PMC
      // pagination can run long for broad terms) and returns a job_id
      // immediately; progress streams back over the job_progress WS channel.
      const { job_id: jobId } = await RegulifyService.cle_get_search_count(
        studyName,
        query,
      );
      if (!jobId) {
        throw new Error("Search did not return a job id");
      }

      await subscribeToJob(jobId, {
        onUpdate: (job) => setLoadingProgress(job.progress_pct, job.message),
        onComplete: (result) => {
          const resultCount = typeof result?.litr_count === "number" ? result.litr_count : 0;
          setHistory((prev) => [
            { query, resultCount, timestamp: new Date().toISOString() },
            ...prev,
          ]);
          setLoading(false);
        },
        onError: (message) => {
          toast.error(message || "Literature search failed");
          setLoading(false);
        },
      });
    } catch (err) {
      console.error("Failed to run search:", err);
      toast.error("Failed to start literature search");
      setLoading(false);
    }
  };

  const handleUseHistory = (queryFromHistory) => {
    setQuery(queryFromHistory); // 👈 inject back into Query Box
  };

  const handleDelete = (idx) => {
    const newHistory = [...history];
    newHistory.splice(idx, 1);
    setHistory(newHistory);
  };

  return (
    <div className="h-screen flex flex-col">
      {/* QueryBox at top with natural height */}
      <div className="p-2">
        <QueryBox
          query={query}
          setQuery={setQuery}
          onSearch={handleSearch}
          picoQuery={picoQuery}
        />
      </div>

      {/* History fills remaining space */}
      <div className="flex-1 min-h-0 p-4">
        <HistoryAndDetails
          historyItems={history}
          onUseHistory={handleUseHistory}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );

}
